import React from 'react';

import { decimalUpTo } from 'Helpers/currencyFormat';
import { formatFigure } from 'Helpers/formatService';

import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

const fmt = (value) => decimalUpTo(Number(value) || 0);

/**
 * RAG matrix format (IB-681): impact (rows, most-severe first) × probability
 * (columns), each cell the count of risks in that gross/residual pair. Cells
 * shade by combined severity (impact rank × probability percentage) using the
 * RADIO palette (amber = the red 50% tint #F88092). A footnote reports risks
 * excluded for lacking the relevant impact/probability pair.
 */
const RadioRagMatrix = ({ data, loading }) => {
    if (loading) {
        return (
            <table className="inbud-table">
                <tbody>
                    <TableRowSkeleton rows={5} columns={5} />
                </tbody>
            </table>
        );
    }

    const impacts = data?.impacts ?? [];
    const probabilities = data?.probabilities ?? [];
    const cells = data?.cells ?? {};
    const excluded = data?.excluded_no_pair ?? 0;

    if (impacts.length === 0 || probabilities.length === 0) {
        return <div className="tw-p-4 tw-text-sm">{$t('No data available')}</div>;
    }

    const orderedImpacts = [...impacts].sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0));
    const maxSeverity = Math.max(...impacts.map((impact) => impact.rank ?? 0)) * Math.max(...probabilities.map((probability) => probability.percentage ?? 0));

    const cellStyle = (impact, probability, count) => {
        if (!count) return {};
        const severity = (impact.rank ?? 0) * (probability.percentage ?? 0);
        const ratio = maxSeverity ? severity / maxSeverity : 0;
        if (ratio >= 0.66) return { backgroundColor: '#c40001', color: '#fff' };
        if (ratio >= 0.33) return { backgroundColor: '#F88092' };
        return { backgroundColor: '#00904a', color: '#fff' };
    };

    return (
        <div className="tw-space-y-3">
            <div className="inbud-table_wrapper tw-overflow-auto">
                <table className="inbud-table">
                    <thead>
                        <tr className="*:!tw-bg-white *:tw-border-b-2 *:tw-border-r-2">
                            <th className="tw-min-w-40">{$t('Impact / Probability')}</th>
                            {probabilities.map((probability) => (
                                <th key={probability.id} className="tw-min-w-24 tw-text-center">
                                    {probability.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {orderedImpacts.map((impact) => (
                            <tr key={impact.id}>
                                <td className="!tw-bg-charcoal-15 tw-font-semibold">{impact.label}</td>
                                {probabilities.map((probability) => {
                                    const count = cells?.[impact.id]?.[probability.id] ?? 0;

                                    return (
                                        <td
                                            key={probability.id}
                                            className="inbud-numeric-cell tw-text-center tw-font-semibold"
                                            style={cellStyle(impact, probability, count)}
                                        >
                                            {formatFigure(count, fmt, { precision: 3 })}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {excluded > 0 && (
                <p className="tw-text-xs tw-text-charcoal-75">
                    {$t('Excluded — no impact/probability pair on the selected basis')}: {fmt(excluded)}
                </p>
            )}
        </div>
    );
};

export default RadioRagMatrix;
